/**
 * Task Automation ("Tasks" / Ballerine Studio) — shared contract constants (BAL-7917).
 *
 * Single source of truth for the string-literal domains behind the `workforce_*` tables.
 * The Drizzle `pgEnum`s (unified-api `workforce-schema.ts`) and the FE Zod / BE TypeBox
 * schemas all reference THESE arrays, so the DB enum, the API contract, and the UI can never
 * drift. `risk_type` is deliberately NOT a pgEnum — it is a `varchar` validated at write against
 * {@link WORKFORCE_RISK_TYPES} (a new risk type is a code change, not an `ALTER TYPE`).
 *
 * DB-prefix `workforce_` (the existing `tasks` table is the CSV-batch one); product/display name
 * is "Tasks". See `docs/plans/BAL-7917-task-automation-backend.md` (locked schema).
 */

// Lifecycle — standard Kanban (K13). Terminal = { done, cancelled }. FE↔BE status is 1:1 (B11).
// pending_review/in_review (BAL-8059, four-eye): worker finishes → pending_review; the OWNER
// picks it up → in_review → done (or sends back to in_progress). Review is OPT-IN — the direct
// in_progress → done path stays legal. Appended (not inserted) so the migration is a plain
// `ALTER TYPE … ADD VALUE` — pgEnum order is not load-bearing anywhere.
export const WORKFORCE_TASK_STATUSES = [
  'to_do',
  'in_progress',
  'done',
  'cancelled',
  'pending_review',
  'in_review',
] as const;
export type WorkforceTaskStatus = (typeof WORKFORCE_TASK_STATUSES)[number];

// A task in one of these is closed; leaving one (terminal → active) is a "reopen" (B7).
export const WORKFORCE_TERMINAL_STATUSES = [
  'done',
  'cancelled',
] as const satisfies readonly WorkforceTaskStatus[];

// Resolution — two fixed outcomes (K8/Δ16). Expandable later via `ALTER TYPE … ADD VALUE`.
export const WORKFORCE_TASK_RESOLUTIONS = ['cleared', 'flagged'] as const;
export type WorkforceTaskResolution = (typeof WORKFORCE_TASK_RESOLUTIONS)[number];

// Event — "what happened" (J8/Δ17). Rule listens on it (execution); template tags it (observability).
// Phase-1 wires 'report.published'; 'report.moved_to_qc'/'report.deleted' drive task cleanup; rest reserved.
export const WORKFORCE_EVENTS = [
  'report.published',
  'report.moved_to_qc',
  'report.deleted',
  'manual',
  'scheduled',
  'task.completed',
] as const;
export type WorkforceEvent = (typeof WORKFORCE_EVENTS)[number];

// Idempotency key-composition strategy — owned by the rule (J9). Default 'report'. No json-logic 'custom' (J15).
export const WORKFORCE_IDEMPOTENCY_SCOPES = ['report', 'report_risk_type', 'business'] as const;
export type WorkforceIdempotencyScope = (typeof WORKFORCE_IDEMPOTENCY_SCOPES)[number];

// Canonical risk taxonomy. Stored as VARCHAR, app-validated — NOT a pgEnum (B5). The first five MUST
// stay in lockstep with the keys of `RiskTypesSchema` (schemas/report-schema.ts) — DON'T hand-copy a
// separate vocabulary (E20); a parity unit test asserts it. `policyFit` is the separate policy-fit
// signal (not part of RiskTypesSchema). camelCase to match the schema keys.
export const WORKFORCE_RISK_TYPES = [
  'legal',
  'chargebacks',
  'reputational',
  'contentFindings',
  'transactionLaundering',
  'policyFit',
] as const;
export type WorkforceRiskType = (typeof WORKFORCE_RISK_TYPES)[number];

// Decision (gates readiness when required) vs action (a do-a-thing side task).
export const WORKFORCE_TASK_KINDS = ['decision', 'action'] as const;
export type WorkforceTaskKind = (typeof WORKFORCE_TASK_KINDS)[number];

// Authoritative provenance of a task (Δ37) — the SIGNAL, replacing "manual = definition_id IS NULL".
// 'trigger' = the engine, 'manual' = a user-created task. A backfill replays publishes THROUGH the
// engine, so it stamps 'trigger' too (identical aging/idempotency) — no separate value. Read `origin`,
// never infer from nulls.
export const WORKFORCE_TASK_ORIGINS = ['trigger', 'manual'] as const;
export type WorkforceTaskOrigin = (typeof WORKFORCE_TASK_ORIGINS)[number];

// Condition det/LLM marker (K3). Phase-1 evaluates 'deterministic' only; 'llm' accepted-but-unbuilt.
export const WORKFORCE_CONDITION_TYPES = ['deterministic', 'llm'] as const;
export type WorkforceConditionType = (typeof WORKFORCE_CONDITION_TYPES)[number];

// Actor model (B12/F17) — who performed a mutation / owns a task / authored a comment.
// Phase-1 writes 'user' (session) + 'system' (the trigger); 'agent' is RESERVED for copilot (zero migration).
export const WORKFORCE_ACTOR_TYPES = ['user', 'agent', 'system'] as const;
export type WorkforceActorType = (typeof WORKFORCE_ACTOR_TYPES)[number];

// The actor performing a mutation / authoring a comment. `id` is null for the system actor.
export type WorkforceActor = { type: WorkforceActorType; id: string | null };

// HITL agent-execution policy per task type (F18). Phase-1 default 'forbidden'; 'propose'/'auto' reserved.
export const WORKFORCE_AGENT_EXECUTIONS = ['forbidden', 'propose', 'auto'] as const;
export type WorkforceAgentExecution = (typeof WORKFORCE_AGENT_EXECUTIONS)[number];

// Task↔task relationships. Phase-1 emits 'subtask' + 'follow_up'; the rest are human-authored links.
export const WORKFORCE_TASK_RELATIONSHIPS = [
  'subtask',
  'blocks',
  'blocked_by',
  'follow_up',
  'relates_to',
  'duplicate_of',
] as const;
export type WorkforceTaskRelationship = (typeof WORKFORCE_TASK_RELATIONSHIPS)[number];

// How a rule participates when several target the same report-type group (K18).
//   'all'  (default): fires independently iff its condition is truthy.
//   'first'         : priority-ordered; only the first truthy rule in the group fires.
export const WORKFORCE_TASK_RULE_MATCHES = ['first', 'all'] as const;
export type WorkforceTaskRuleMatch = (typeof WORKFORCE_TASK_RULE_MATCHES)[number];

// Explicit parent/child role, declared by the emit and stored on the task (D3). Readiness + the
// report overlay derive off `role`, NOT off `risk_type IS NULL` (never overload risk_type as "parent").
//   parent     — the alert-level decision task (fans out children)
//   subtask    — a child of a parent (the emit's relations wire it)
//   standalone — a task with no parent/child graph (manual tasks, default)
export const WORKFORCE_TASK_ROLES = ['parent', 'subtask', 'standalone'] as const;
export type WorkforceTaskRole = (typeof WORKFORCE_TASK_ROLES)[number];

// Typed audit-action catalog (Y4). `audit_logs.action` for entity_type='workforce_task' MUST be one of
// these; the audit row is written in the SAME txn as the mutation. This is a customer-visible contract
// via the audit endpoint — additive only (never rename/remove a value).
export const WORKFORCE_TASK_EVENTS = [
  'task.created',
  'task.status_changed',
  'task.resolution_set',
  'task.resolution_changed',
  'task.reopened',
  'task.cancelled',
  'task.deleted',
  'task.assignment_changed',
  'task.owner_changed',
  'task.comment_added',
  'task.attachment_added',
  'task.attachment_removed',
  'task.link_added',
  'task.link_removed',
] as const;
export type WorkforceTaskEvent = (typeof WORKFORCE_TASK_EVENTS)[number];
